import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  PenLine,
  CheckCircle2,
  AlertTriangle,
  Eraser,
  QrCode,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthNamesEs = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const monthNamesEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');
const lastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ---- Signature canvas (no external deps, mouse + touch) ----
const SignaturePad = ({ canvasRef, onChange }) => {
  const drawing = useRef(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const end = (e) => {
    if (!drawing.current) return;
    e && e.preventDefault();
    drawing.current = false;
    onChange && onChange();
  };

  return (
    <canvas
      ref={canvasRef}
      width={500}
      height={200}
      className="w-full h-[200px] bg-white rounded-lg border-2 border-dashed border-slate-300 touch-none cursor-crosshair"
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
    />
  );
};

const MiJornada = () => {
  const { language } = useLanguage();
  const monthNames = language === 'es' ? monthNamesEs : monthNamesEn;

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [totals, setTotals] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signed, setSigned] = useState(false);
  const [upToDate, setUpToDate] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);

  // QR de fichaje del trabajador (modo "scanner": el centro lo lee)
  const [qrConfig, setQrConfig] = useState(null);
  const [myQr, setMyQr] = useState(null); // {token, employee_id, period, seconds_left}
  const [qrSeconds, setQrSeconds] = useState(0);

  const period = `${year}-${pad(month + 1)}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = `${year}-${pad(month + 1)}-01`;
      const endDate = `${year}-${pad(month + 1)}-${pad(lastDayOfMonth(year, month))}`;

      const [summaryRes, sigRes] = await Promise.all([
        axios.get(`${API_URL}/reports/summary`, { params: { start_date: startDate, end_date: endDate } }),
        axios.get(`${API_URL}/signatures/my`, { params: { period } }),
      ]);

      setTotals(summaryRes.data?.totals || null);
      setSigned(sigRes.data?.signed || false);
      setSignature(sigRes.data?.signature || null);
      setUpToDate(sigRes.data?.up_to_date !== false);
    } catch (error) {
      console.error('Error fetching jornada:', error);
    } finally {
      setLoading(false);
    }
  }, [year, month, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Cargar configuración QR de la organización
  useEffect(() => {
    axios.get(`${API_URL}/qr/config`)
      .then((res) => setQrConfig(res.data))
      .catch(() => setQrConfig(null));
  }, []);

  const qrEnabledForWorker = qrConfig?.qr_enabled && ['scanner', 'both'].includes(qrConfig?.qr_mode);

  const fetchMyToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/qr/my-token`);
      setMyQr(res.data);
      setQrSeconds(res.data.seconds_left);
    } catch {
      setMyQr(null);
    }
  }, []);

  // Generar y refrescar el token del trabajador
  useEffect(() => {
    if (!qrEnabledForWorker) return;
    fetchMyToken();
    const t = setInterval(() => {
      setQrSeconds((s) => {
        if (s <= 1) { fetchMyToken(); return myQr?.period || 30; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [qrEnabledForWorker, fetchMyToken, myQr?.period]);

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); } else { setMonth(month - 1); }
  };
  const goNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); } else { setMonth(month + 1); }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasDrawn(false);
  };

  const openModal = () => {
    setConfirmed(false);
    setHasDrawn(false);
    setShowModal(true);
    // clear after canvas mounts
    setTimeout(clearCanvas, 50);
  };

  const submitSignature = async () => {
    if (!confirmed || !hasDrawn) return;
    setSubmitting(true);
    try {
      const signature_image = canvasRef.current.toDataURL('image/png');
      await axios.post(`${API_URL}/signatures`, {
        period,
        signature_image,
        worked_hours: totals?.worked_hours || 0,
        expected_hours: totals?.expected_hours || 0,
        extra_hours: totals?.extra_hours || 0,
      });
      setShowModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error signing:', error);
      alert(language === 'es' ? 'Error al firmar. Inténtalo de nuevo.' : 'Error signing. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(language === 'es' ? 'es-ES' : 'en-US');
    } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="mi-jornada-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
          {language === 'es' ? 'Mi Jornada' : 'My Timesheet'}
        </h1>
        <p className="text-slate-500">
          {language === 'es'
            ? 'Revisa y firma tu resumen mensual de horas'
            : 'Review and sign your monthly hours summary'}
        </p>
      </div>

      {/* Month selector */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              {monthNames[month]} {year}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={goPrev}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={goNext}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-500">{language === 'es' ? 'Horas Trabajadas' : 'Worked Hours'}</p>
                <p className="text-2xl font-bold text-slate-900">{totals?.worked_hours_formatted || '0:00'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-500">{language === 'es' ? 'Horas Previstas' : 'Expected Hours'}</p>
                <p className="text-2xl font-bold text-slate-900">{totals?.expected_hours_formatted || '0:00'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-500">{language === 'es' ? 'Balance (Extra)' : 'Balance (Extra)'}</p>
                <p className="text-2xl font-bold text-slate-900">{totals?.extra_hours_formatted || '0:00'}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signature status */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
            <PenLine className="w-5 h-5 text-indigo-600" />
            {language === 'es' ? 'Conformidad / Firma' : 'Approval / Signature'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {signed ? (
            <div className="space-y-4">
              <div className={`flex items-start gap-3 p-4 rounded-lg border ${upToDate ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                {upToDate ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${upToDate ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {upToDate
                      ? (language === 'es' ? 'Jornada firmada' : 'Timesheet signed')
                      : (language === 'es' ? 'Firma desactualizada: los fichajes han cambiado desde que firmaste' : 'Signature outdated: records changed since you signed')}
                  </p>
                  <p className="text-sm text-slate-500">
                    {language === 'es' ? 'Firmado el ' : 'Signed on '}{fmtDateTime(signature?.signed_at)}
                    {signature?.signer_name ? ` · ${signature.signer_name}` : ''}
                  </p>
                </div>
              </div>
              {signature?.signature_image && (
                <div className="inline-block p-2 bg-white border border-slate-200 rounded-lg">
                  <img src={signature.signature_image} alt="firma" className="h-24" />
                </div>
              )}
              {!upToDate && (
                <Button onClick={openModal} variant="outline">
                  <PenLine className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Volver a firmar' : 'Sign again'}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {language === 'es'
                  ? 'Al firmar confirmas que el resumen de horas mostrado arriba es correcto.'
                  : 'By signing you confirm the hours summary shown above is correct.'}
              </p>
              <Button onClick={openModal} disabled={loading || !totals} className="bg-indigo-600 hover:bg-indigo-700">
                <PenLine className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Firmar mi jornada' : 'Sign my timesheet'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR de fichaje del trabajador (modo lector) */}
      {qrEnabledForWorker && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-600" />
              {language === 'es' ? 'Mi QR de fichaje' : 'My clock-in QR'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              {myQr ? (
                <>
                  <div className="p-3 bg-white border border-slate-200 rounded-xl">
                    <QRCodeSVG value={`MJQR1|${myQr.employee_id}|${myQr.token}`} size={220} level="M" includeMargin />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="w-40 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 transition-all duration-1000 ease-linear"
                        style={{ width: `${(qrSeconds / (myQr.period || 30)) * 100}%` }}
                      />
                    </div>
                    <span className="text-slate-400 text-sm tabular-nums">{qrSeconds}s</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-3 text-center max-w-xs">
                    {language === 'es'
                      ? 'Muestra este código al lector de tu centro para fichar. Se renueva solo por seguridad.'
                      : 'Show this code to your workplace reader to clock in. It refreshes automatically for security.'}
                  </p>
                </>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400">
                  <div className="spinner" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !submitting && setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold font-['Manrope'] text-slate-900 mb-1">
              {language === 'es' ? `Firmar ${monthNames[month]} ${year}` : `Sign ${monthNames[month]} ${year}`}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              {language === 'es' ? 'Dibuja tu firma en el recuadro.' : 'Draw your signature in the box.'}
            </p>

            <SignaturePad canvasRef={canvasRef} onChange={() => setHasDrawn(true)} />

            <div className="flex justify-end mt-2">
              <Button variant="ghost" size="sm" onClick={clearCanvas}>
                <Eraser className="w-4 h-4 mr-1" />
                {language === 'es' ? 'Borrar' : 'Clear'}
              </Button>
            </div>

            <label className="flex items-start gap-2 mt-3 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
              <span>
                {language === 'es'
                  ? `Confirmo que mi resumen de horas de ${monthNames[month]} ${year} es correcto.`
                  : `I confirm my hours summary for ${monthNames[month]} ${year} is correct.`}
              </span>
            </label>

            <div className="flex items-center justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={submitting}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                onClick={submitSignature}
                disabled={!confirmed || !hasDrawn || submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting
                  ? (language === 'es' ? 'Firmando...' : 'Signing...')
                  : (language === 'es' ? 'Firmar' : 'Sign')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiJornada;
